// SynastryScreen - Main screen for relationship compatibility feature
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles, RotateCcw, Save, Check, ChevronLeft, Users, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { MarkdownText } from '../common/MarkdownText';
import { PageContainer } from '../layout/PageContainer';
import { Button } from '../common/Button';
import { PageLoader } from '../common/LoadingSpinner';
import { useSynastry } from '../../hooks/useSynastry';
import { useBirthChart } from '../../hooks/useBirthChart';
import { useTone } from '../../hooks/useTone';
import { AspectGrid } from '../synastry/AspectGrid';

export const SynastryScreen = ({ currentUser }) => {
    const { promptPrefix } = useTone();
    const {
        personA, setPersonA,
        personB, setPersonB,
        locationResults,
        isSearchingLocation,
        activePersonForLocation,
        searchLocation,
        selectLocation,
        loadPersonAFromChart,
        synastryData,
        interpretation,
        currentStep,
        nextStep,
        prevStep,
        canProceed,
        isLoading,
        isLoadingInterpretation,
        isSaving,
        isSaved,
        error,
        setError,
        calculateSynastry,
        getInterpretation,
        saveSynastry,
        reset,
        savedReadings,
        loadSavedReadings,
        loadSavedReading
    } = useSynastry(currentUser, promptPrefix);

    // Get saved charts for Person A selection
    const { savedCharts, loadSavedCharts } = useBirthChart(currentUser, promptPrefix);

    // Load saved charts on mount
    useEffect(() => {
        if (currentUser && !currentUser.isAnonymous) {
            loadSavedCharts();
            loadSavedReadings();
        }
    }, [currentUser, loadSavedCharts, loadSavedReadings]);

    // Save modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [readingName, setReadingName] = useState('');

    // Location search state
    const [personALocationQuery, setPersonALocationQuery] = useState('');
    const [personBLocationQuery, setPersonBLocationQuery] = useState('');

    // Saved readings panel state
    const [showSavedReadings, setShowSavedReadings] = useState(false);

    // Handle calculate and auto-get interpretation
    const handleCalculate = useCallback(async () => {
        const data = await calculateSynastry();
        if (data) {
            getInterpretation(data);
        }
    }, [calculateSynastry, getInterpretation]);

    // Handle save
    const handleSave = useCallback(async () => {
        const result = await saveSynastry(readingName || `${personA.name} & ${personB.name}`);
        if (result) {
            setShowSaveModal(false);
        }
    }, [saveSynastry, readingName, personA.name, personB.name]);

    // Render step content
    const renderStepContent = () => {
        // Loading state
        if (isLoading) {
            return (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                    <PageLoader text="Weaving the cosmic threads..." />
                    <motion.div
                        className="mt-6 text-theme-muted text-center max-w-xs"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        Analyzing the celestial dance between your charts...
                    </motion.div>
                </motion.div>
            );
        }

        // Results view
        if (synastryData) {
            return (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                >
                    {/* Header */}
                    <div className="text-center">
                        <motion.h1
                            className="text-2xl sm:text-3xl font-serif mb-2"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <span className="bg-gradient-to-r from-pink-200 via-purple-200 to-pink-300 bg-clip-text text-transparent">
                                {synastryData.personA.name} & {synastryData.personB.name}
                            </span>
                        </motion.h1>
                    </div>

                    {/* Compatibility Score */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex justify-center"
                    >
                        <div className="relative w-32 h-32 sm:w-40 sm:h-40">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle
                                    cx="50%"
                                    cy="50%"
                                    r="45%"
                                    fill="none"
                                    stroke="rgba(168, 85, 247, 0.2)"
                                    strokeWidth="8"
                                />
                                <circle
                                    cx="50%"
                                    cy="50%"
                                    r="45%"
                                    fill="none"
                                    stroke="url(#compatGradient)"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeDasharray={`${synastryData.compatibility.score * 2.83} 283`}
                                />
                                <defs>
                                    <linearGradient id="compatGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#ec4899" />
                                        <stop offset="100%" stopColor="#a855f7" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl sm:text-4xl font-bold text-amber-100">
                                    {synastryData.compatibility.score}%
                                </span>
                                <span className="text-purple-300/70 text-xs">Compatibility</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Big Three Comparison */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="grid grid-cols-2 gap-4"
                    >
                        <PersonSummary
                            name={synastryData.personA.name}
                            bigThree={synastryData.bigThree.personA}
                        />
                        <PersonSummary
                            name={synastryData.personB.name}
                            bigThree={synastryData.bigThree.personB}
                        />
                    </motion.div>

                    {/* Aspect Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="grid grid-cols-3 gap-3"
                    >
                        <StatCard label="Total Aspects" value={synastryData.compatibility.totalAspects} />
                        <StatCard label="Harmonious" value={synastryData.compatibility.harmonious} color="text-green-400" />
                        <StatCard label="Challenging" value={synastryData.compatibility.challenging} color="text-orange-400" />
                    </motion.div>

                    {/* Aspect Grid */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <AspectGrid aspects={synastryData.topAspects} />
                    </motion.div>

                    {/* Interpretation */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="p-5 bg-theme-glass backdrop-blur-sm border border-theme-glass rounded-2xl"
                    >
                        <div className="flex items-center gap-2 text-theme-primary font-semibold mb-4">
                            <Heart className="w-5 h-5" />
                            Cosmic Connection Reading
                        </div>

                        {isLoadingInterpretation ? (
                            <div className="space-y-3">
                                {[...Array(4)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="h-4 bg-purple-700/30 rounded animate-pulse"
                                        style={{ width: `${85 - i * 10}%` }}
                                    />
                                ))}
                            </div>
                        ) : interpretation ? (
                            <MarkdownText size="sm">
                                {interpretation}
                            </MarkdownText>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={() => getInterpretation()}
                                icon={Sparkles}
                            >
                                Generate Reading
                            </Button>
                        )}
                    </motion.div>

                    {/* Actions */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                        className="flex flex-wrap justify-center gap-3"
                    >
                        {currentUser && !currentUser.isAnonymous && (
                            isSaved ? (
                                <Button
                                    variant="secondary"
                                    size="md"
                                    disabled
                                    icon={Check}
                                    className="border-green-500/30 text-green-400"
                                >
                                    Saved!
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => {
                                        setReadingName(`${synastryData.personA.name} & ${synastryData.personB.name}`);
                                        setShowSaveModal(true);
                                    }}
                                    icon={Save}
                                >
                                    Save Reading
                                </Button>
                            )
                        )}
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={reset}
                            icon={RotateCcw}
                        >
                            New Reading
                        </Button>
                    </motion.div>
                </motion.div>
            );
        }

        // Form steps
        return (
            <div className="min-h-[70vh] flex flex-col justify-center">
                {/* Title */}
                <motion.div
                    className="text-center mb-6"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-2xl sm:text-3xl font-serif mb-2">
                        <span className="bg-gradient-to-r from-pink-200 via-purple-200 to-pink-300 bg-clip-text text-transparent">
                            Cosmic Connection
                        </span>
                    </h1>
                    <p className="text-theme-muted text-sm">Discover your compatibility</p>
                </motion.div>

                {/* Step indicator */}
                <div className="flex justify-center gap-2 mb-6">
                    {[0, 1, 2].map((step) => (
                        <div
                            key={step}
                            className={`w-3 h-3 rounded-full transition-all ${currentStep === step
                                ? 'bg-theme-accent scale-125'
                                : currentStep > step
                                    ? 'bg-theme-primary'
                                    : 'bg-theme-secondary'
                                }`}
                        />
                    ))}
                </div>

                {/* Error message */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-center text-sm"
                    >
                        {error}
                        <button onClick={() => setError('')} className="ml-3">×</button>
                    </motion.div>
                )}

                {/* Step content */}
                <AnimatePresence mode="wait">
                    {currentStep === 0 && (
                        <PersonAStep
                            key="step0"
                            personA={personA}
                            setPersonA={setPersonA}
                            savedCharts={savedCharts}
                            loadPersonAFromChart={loadPersonAFromChart}
                            currentUser={currentUser}
                            locationQuery={personALocationQuery}
                            setLocationQuery={setPersonALocationQuery}
                            locationResults={locationResults}
                            isSearchingLocation={isSearchingLocation}
                            activePersonForLocation={activePersonForLocation}
                            searchLocation={searchLocation}
                            selectLocation={selectLocation}
                        />
                    )}
                    {currentStep === 1 && (
                        <PersonBStep
                            key="step1"
                            personB={personB}
                            setPersonB={setPersonB}
                            locationQuery={personBLocationQuery}
                            setLocationQuery={setPersonBLocationQuery}
                            locationResults={locationResults}
                            isSearchingLocation={isSearchingLocation}
                            activePersonForLocation={activePersonForLocation}
                            searchLocation={searchLocation}
                            selectLocation={selectLocation}
                        />
                    )}
                    {currentStep === 2 && (
                        <ConfirmStep
                            key="step2"
                            personA={personA}
                            personB={personB}
                        />
                    )}
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex justify-center gap-4 mt-6">
                    {currentStep > 0 && (
                        <Button
                            variant="ghost"
                            size="md"
                            onClick={prevStep}
                            icon={ChevronLeft}
                        >
                            Back
                        </Button>
                    )}
                    {currentStep < 2 ? (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={nextStep}
                            disabled={!canProceed}
                            className="min-w-[140px]"
                        >
                            Continue
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleCalculate}
                            icon={Heart}
                            className="min-w-[180px]"
                        >
                            Reveal Connection
                        </Button>
                    )}
                </div>

                {/* Saved Readings Panel - Prominent */}
                {currentUser && !currentUser.isAnonymous && savedReadings.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-8 p-4 bg-gradient-to-br from-pink-900/30 via-purple-900/30 to-purple-900/30 border border-pink-500/30 rounded-2xl"
                    >
                        <button
                            onClick={() => setShowSavedReadings(!showSavedReadings)}
                            className="w-full flex items-center justify-between gap-2 text-pink-200 hover:text-pink-100 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Heart className="w-5 h-5 text-pink-400" />
                                <span className="font-medium">My Saved Readings</span>
                                <span className="px-2 py-0.5 bg-pink-500/30 text-pink-300 text-xs rounded-full">
                                    {savedReadings.length}
                                </span>
                            </div>
                            {showSavedReadings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>

                        <AnimatePresence>
                            {showSavedReadings && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-4 space-y-2 max-h-60 overflow-y-auto"
                                >
                                    {savedReadings.map((reading) => (
                                        <button
                                            key={reading.id}
                                            onClick={() => loadSavedReading(reading)}
                                            className="w-full p-3 bg-purple-900/50 border border-purple-500/30 rounded-xl text-left hover:bg-purple-800/50 hover:border-pink-400/40 transition-all group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="text-amber-100 font-medium text-sm group-hover:text-pink-200 transition-colors">
                                                    {reading.name || 'Unnamed Reading'}
                                                </div>
                                                <div className="text-pink-400 font-bold text-sm">
                                                    {reading.synastryData?.compatibility?.score}%
                                                </div>
                                            </div>
                                            <div className="text-purple-400/60 text-xs mt-1">
                                                Tap to view
                                            </div>
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        );
    };

    return (
        <PageContainer className="py-8">
            <AnimatePresence mode="wait">
                {renderStepContent()}
            </AnimatePresence>

            {/* Save modal */}
            <AnimatePresence>
                {showSaveModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        <div
                            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                            onClick={() => setShowSaveModal(false)}
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative w-full max-w-sm p-6 bg-gradient-to-br from-purple-900/95 via-purple-950/95 to-indigo-950/95 border border-purple-500/30 rounded-2xl"
                        >
                            <h3 className="text-xl font-serif text-amber-100 mb-4">Save Reading</h3>
                            <input
                                type="text"
                                value={readingName}
                                onChange={(e) => setReadingName(e.target.value)}
                                placeholder="Name this reading..."
                                className="w-full px-4 py-3 mb-4 bg-purple-900/50 border border-purple-500/30 rounded-xl text-amber-100 placeholder-purple-400/50 focus:outline-none focus:border-pink-400/50"
                            />
                            <div className="flex gap-3">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => setShowSaveModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleSave}
                                    loading={isSaving}
                                    icon={Save}
                                    className="flex-1"
                                >
                                    Save
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PageContainer>
    );
};

const hasScreenCoordinates = (person) =>
    typeof person?.latitude === 'number' &&
    Number.isFinite(person.latitude) &&
    typeof person?.longitude === 'number' &&
    Number.isFinite(person.longitude);

const isReadyForStep = (person) =>
    Boolean(person?.birthDate && (person.unknownTime || person.birthTime) && hasScreenCoordinates(person));

const sharedInputClass = 'w-full px-4 py-3 input-theme rounded-xl focus:outline-none focus:border-theme-primary';

const UnknownTimeToggle = ({ checked, onChange }) => (
    <label className="flex items-center gap-3 px-4 py-3 input-theme rounded-xl cursor-pointer">
        <span className={`flex h-5 w-5 items-center justify-center rounded border ${checked
            ? 'bg-theme-accent border-theme-accent text-purple-950'
            : 'border-theme-glass text-transparent'
            }`}>
            <Check className="w-3 h-3" />
        </span>
        <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            className="sr-only"
        />
        <span className="text-sm text-theme-secondary">Birth time unknown</span>
    </label>
);

const LocationSearchField = ({
    forPerson,
    query,
    setQuery,
    selectedLocation,
    locationResults,
    isSearchingLocation,
    activePersonForLocation,
    searchLocation,
    selectLocation
}) => {
    useEffect(() => {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length < 2) {
            searchLocation('', forPerson);
            return undefined;
        }

        const timeout = setTimeout(() => {
            searchLocation(trimmedQuery, forPerson);
        }, 350);

        return () => clearTimeout(timeout);
    }, [forPerson, query, searchLocation]);

    const isActive = activePersonForLocation === forPerson;

    return (
        <div className="space-y-2">
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Birth location (city, country)"
                    className={sharedInputClass}
                />
                {isSearchingLocation && isActive && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
                {isActive && locationResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-purple-900/95 border border-purple-500/30 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                        {locationResults.map((loc) => (
                            <button
                                key={`${loc.displayName}-${loc.latitude}-${loc.longitude}`}
                                type="button"
                                onClick={() => {
                                    selectLocation(loc, forPerson);
                                    setQuery(loc.displayName.split(',')[0]);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-purple-200 hover:bg-purple-800/50"
                            >
                                {loc.displayName}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {selectedLocation && (
                <p className="flex items-center gap-1 text-xs text-theme-muted truncate">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{selectedLocation}</span>
                </p>
            )}
        </div>
    );
};

// Person A step - Select from saved charts or enter manually
const PersonAStep = ({
    personA,
    setPersonA,
    savedCharts,
    loadPersonAFromChart,
    currentUser,
    locationQuery,
    setLocationQuery,
    locationResults,
    isSearchingLocation,
    activePersonForLocation,
    searchLocation,
    selectLocation
}) => (
    <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-md mx-auto space-y-4"
    >
        <div className="text-center mb-4">
            <Users className="w-8 h-8 mx-auto text-theme-primary mb-2" />
            <h2 className="text-lg font-serif text-theme">Your Information</h2>
        </div>

        {/* Saved charts quick select */}
        {currentUser && !currentUser.isAnonymous && savedCharts.length > 0 && (
            <div className="space-y-2">
                <p className="text-theme-muted text-sm text-center">Use a saved chart:</p>
                <div className="flex flex-wrap justify-center gap-2">
                    {savedCharts.slice(0, 3).map((chart) => (
                        <button
                            key={chart.id}
                            type="button"
                            onClick={() => {
                                loadPersonAFromChart(chart);
                                setLocationQuery(chart.birthLocation || '');
                            }}
                            className={`px-4 py-2 rounded-lg text-sm transition-all ${personA.name === chart.name
                                ? 'bg-theme-accent-30 border-theme-accent text-theme-accent'
                                : 'bg-theme-glass border-theme-glass text-theme-secondary hover:bg-theme-glass'
                                } border`}
                        >
                            {chart.name}
                        </button>
                    ))}
                </div>
            </div>
        )}

        <div className="text-center text-theme-muted text-xs">or enter manually</div>

        {/* Manual input */}
        <div className="space-y-3">
            <input
                type="text"
                value={personA.name}
                onChange={(e) => setPersonA(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your name"
                className={sharedInputClass}
            />
            <input
                type="date"
                value={personA.birthDate}
                onChange={(e) => setPersonA(prev => ({ ...prev, birthDate: e.target.value }))}
                className={sharedInputClass}
            />
            <input
                type="time"
                value={personA.birthTime}
                disabled={personA.unknownTime}
                onChange={(e) => setPersonA(prev => ({ ...prev, birthTime: e.target.value }))}
                className={`${sharedInputClass} ${personA.unknownTime ? 'opacity-50' : ''}`}
            />
            <UnknownTimeToggle
                checked={personA.unknownTime}
                onChange={(checked) => setPersonA(prev => ({
                    ...prev,
                    unknownTime: checked,
                    birthTime: checked ? '' : prev.birthTime
                }))}
            />
            <LocationSearchField
                forPerson="A"
                query={locationQuery}
                setQuery={setLocationQuery}
                selectedLocation={personA.location}
                locationResults={locationResults}
                isSearchingLocation={isSearchingLocation}
                activePersonForLocation={activePersonForLocation}
                searchLocation={searchLocation}
                selectLocation={selectLocation}
            />
        </div>

        <p className="text-center text-theme-success text-xs min-h-[1rem]">
            {isReadyForStep(personA) && 'Ready to continue'}
        </p>
    </motion.div>
);

// Person B step - Enter partner's information
const PersonBStep = ({
    personB,
    setPersonB,
    locationQuery,
    setLocationQuery,
    locationResults,
    isSearchingLocation,
    activePersonForLocation,
    searchLocation,
    selectLocation
}) => (
    <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-md mx-auto space-y-4"
    >
        <div className="text-center mb-4">
            <Heart className="w-8 h-8 mx-auto text-theme-primary mb-2" />
            <h2 className="text-lg font-serif text-theme">Partner's Information</h2>
        </div>

        <div className="space-y-3">
            <input
                type="text"
                value={personB.name}
                onChange={(e) => setPersonB(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Their name"
                className={sharedInputClass}
            />
            <input
                type="date"
                value={personB.birthDate}
                onChange={(e) => setPersonB(prev => ({ ...prev, birthDate: e.target.value }))}
                className={sharedInputClass}
            />
            <input
                type="time"
                value={personB.birthTime}
                disabled={personB.unknownTime}
                onChange={(e) => setPersonB(prev => ({ ...prev, birthTime: e.target.value }))}
                className={`${sharedInputClass} ${personB.unknownTime ? 'opacity-50' : ''}`}
            />
            <UnknownTimeToggle
                checked={personB.unknownTime}
                onChange={(checked) => setPersonB(prev => ({
                    ...prev,
                    unknownTime: checked,
                    birthTime: checked ? '' : prev.birthTime
                }))}
            />

            <LocationSearchField
                forPerson="B"
                query={locationQuery}
                setQuery={setLocationQuery}
                selectedLocation={personB.location}
                locationResults={locationResults}
                isSearchingLocation={isSearchingLocation}
                activePersonForLocation={activePersonForLocation}
                searchLocation={searchLocation}
                selectLocation={selectLocation}
            />
        </div>
    </motion.div>
);

// Confirm step
const ConfirmStep = ({ personA, personB }) => (
    <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-md mx-auto"
    >
        <div className="text-center mb-6">
            <Sparkles className="w-8 h-8 mx-auto text-pink-400 mb-2" />
            <h2 className="text-lg font-serif text-amber-100">Ready to Reveal</h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-purple-900/40 border border-purple-500/20 rounded-xl text-center">
                <div className="text-amber-100 font-medium mb-1">{personA.name || 'You'}</div>
                <div className="text-purple-300/70 text-xs">{personA.birthDate}</div>
            </div>
            <div className="p-4 bg-purple-900/40 border border-purple-500/20 rounded-xl text-center">
                <div className="text-amber-100 font-medium mb-1">{personB.name || 'Partner'}</div>
                <div className="text-purple-300/70 text-xs">{personB.birthDate}</div>
            </div>
        </div>

        <div className="flex justify-center my-4">
            <Heart className="w-6 h-6 text-pink-400 animate-pulse" />
        </div>
    </motion.div>
);

// Person summary card
const PersonSummary = ({ name, bigThree }) => (
    <div className="p-3 bg-purple-900/40 border border-purple-500/20 rounded-xl text-center">
        <div className="text-amber-100 font-medium text-sm mb-2">{name}</div>
        <div className="text-xs space-y-1">
            <div className="text-purple-300">☉ {bigThree.sun}</div>
            <div className="text-purple-300">☽ {bigThree.moon}</div>
            <div className="text-purple-300">↑ {bigThree.rising}</div>
        </div>
    </div>
);

// Stat card
const StatCard = ({ label, value, color = 'text-amber-100' }) => (
    <div className="p-3 bg-purple-900/40 border border-purple-500/20 rounded-xl text-center">
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        <div className="text-purple-400/70 text-xs">{label}</div>
    </div>
);

export default SynastryScreen;
